/**
 * PillDispatch — the shared queue-until-idle contract for pill-row clicks.
 *
 * Pill-row pills are deliberately NOT gated by the input lock (`PillRow` shows them
 * while an assistant message streams), but their downstream path is a normal
 * self-send through POST /chat/message, which the backend rejects mid-turn with
 * SESSION_BUSY. This module makes every pill click safe during streaming: the click
 * is accepted immediately, visually acknowledged (pending pill + placeholder swap),
 * and executed the moment the current assistant message completes. The in-flight
 * stream is never touched.
 *
 * Contract (design: project-docs/plans/pill-streaming-interaction-design.md):
 * - Idle click → fire immediately (click and fire are the same moment).
 * - Streaming click → single pending slot; the pill shows a pending state and the
 *   input placeholder swaps to the group's `queuedPlaceholder` waiting text.
 * - Re-click of the pending pill → cancel (pill back to standing, placeholder
 *   reverts, stream unaffected).
 * - Click of a DIFFERENT pill while one is pending → last-click-wins swap.
 * - Turn end (`chat:idle` OR `message:complete`) → the pending fire runs exactly
 *   once; the subscription is cleaned up.
 * - Mode switch / mode clear while pending → the slot is dropped (no fire) and the
 *   placeholder reverts. (Session end and new_topic both route through the mode
 *   lifecycle, so they are covered by the same events.)
 *
 * The pending slot is in-memory only: a reload forgets the queued click and the pill
 * rehydrates in standing state from `activePills` (nothing fired, nothing lost).
 *
 * Despite the name, this is shared by BOTH click surfaces that self-send: the
 * pill row (`ActionRouter._buildAgentPill`) and the chat-input Create menu
 * (`ActionRouter.dispatchCreateMenuItem`), via `ActionRouter._dispatchOrFire`.
 * The Create menu has no pending visual of its own — `PillRow.setPillPending` is
 * keyed on pill ids and matches nothing for a menu item — so for menu clicks the
 * placeholder swap is the ONLY acknowledgement, which is why those items carry
 * per-button `queuedPlaceholder` copy.
 */

/**
 * The signals that end a turn. BOTH are required: `chat:idle` fires only for
 * programmatic/animated activity (the `_activityCount` refcount), while an
 * ordinary streamed turn ends with `message:complete`.
 * @type {string[]}
 */
const TURN_END_EVENTS = ['chat:idle', 'message:complete'];

export class PillDispatch {
  /**
   * @param {Object} [deps]
   * @param {EventBus} [deps.eventBus] - App event bus (turn-end / mode lifecycle).
   *   Falls back to `window.eventBus` at call time when omitted.
   * @param {Function} [deps.getChatManager] - Accessor for the ChatManager (programmatic
   *   activity refcount).
   * @param {Function} [deps.getInput] - Accessor for the Input controller: the busy
   *   state (`isSubmitting` / `chatBusy`) and the placeholder API.
   * @param {Function} [deps.getPillRow] - Accessor for the PillRow controller (pending visuals).
   */
  constructor({ eventBus = null, getChatManager = null, getInput = null, getPillRow = null } = {}) {
    this._eventBus = eventBus;
    this._getChatManager = getChatManager || (() => window.app?.modules?.chatManager);
    this._getInput = getInput || (() => window.app?.modules?.input);
    this._getPillRow = getPillRow || (() => window.app?.modules?.pillRow);

    /**
     * The single pending slot (§5.1): at most one queued pill click across the row.
     * @type {{pillId:string, groupId:string, waitingText:string|null, fire:Function}|null}
     */
    this._pending = null;
    /** @type {Function|null} The live chat:idle listener (present iff a slot is pending). */
    this._onIdle = null;

    // Context change cancels a pending click (§5.7): mode switch (incl. new_topic's
    // forced ADVICE re-entry) and mode clear (session end / return to dashboard).
    const bus = this._bus();
    if (bus?.on) {
      bus.on('mode:changed', () => this.clear());
      bus.on('mode:cleared', () => this.clear());
    }
  }

  /** @private @returns {EventBus|null} */
  _bus() {
    return this._eventBus || window.eventBus || null;
  }

  /**
   * Whether a turn is in flight — the gate that decides queue-vs-fire.
   *
   * There are TWO independent busy states and this must honor both (`Input.js`
   * `_isLocked`): `isSubmitting` is set when the user dispatches a real LLM
   * request and cleared on `message:complete`; `chatBusy` tracks programmatic and
   * animated messages via the `chat:busy`/`chat:idle` refcount. Reading only the
   * refcount — as this did originally — sees the programmatic half and is blind to
   * ordinary streaming turns, so every mid-stream click fired immediately and raced
   * the live turn into a SESSION_BUSY rejection.
   *
   * Deliberately NOT `Input._isLocked()`: that also ORs the usage lock, and a
   * quota-blocked user's click must not queue forever waiting for a turn that will
   * never start.
   *
   * @returns {boolean}
   */
  isBusy() {
    const input = this._getInput();
    if (input?.isSubmitting || input?.chatBusy) return true;
    // Direct read of the programmatic refcount: covers the window before Input's
    // `chat:busy` listener has run, and the case where Input is not mounted.
    return (this._getChatManager()?._activityCount || 0) > 0;
  }

  /**
   * Route a pill click. Fires immediately when idle; otherwise manages the single
   * pending slot (queue / cancel-on-re-click / last-click-wins swap).
   *
   * @param {Object} req
   * @param {string} req.pillId - The pill's button id (PillRow pending-state key).
   * @param {string} req.groupId - Owning pill-row group id.
   * @param {string|null} [req.waitingText] - Placeholder text for the queued window
   *   (the group's `queuedPlaceholder`); no swap when absent.
   * @param {Function} req.fire - The action to run at fire time (echo + self-send +
   *   consume). Invoked exactly once, never while the prior turn is still streaming.
   */
  dispatch({ pillId, groupId, waitingText = null, fire }) {
    if (typeof fire !== 'function') return;

    if (!this.isBusy()) {
      fire();
      return;
    }

    // Re-click of the pending pill = cancel (§5.3).
    if (this._pending?.pillId === pillId) {
      this.clear();
      return;
    }

    // New queue entry, or last-click-wins swap (§5.2).
    if (this._pending) this._getPillRow()?.clearPillPending?.();
    this._pending = { pillId, groupId, waitingText, fire };
    this._getPillRow()?.setPillPending?.(pillId);
    if (waitingText) this._getInput()?.setPlaceholder?.(waitingText);
    this._subscribe();

    // Race guard (§8): if the stream settled between the busy check and the
    // subscription, chat:idle has already fired and will not fire again — flush now.
    if (!this.isBusy()) this._fireNow();
  }

  /**
   * Drop the pending slot without firing (cancel / context change). Reverts the
   * pending pill to standing and the placeholder to its default. Idempotent.
   */
  clear() {
    if (!this._pending && !this._onIdle) return;
    this._unsubscribe();
    this._getPillRow()?.clearPillPending?.();
    this._revertPlaceholder();
    this._pending = null;
  }

  /**
   * @private Subscribe the (single) turn-end listener to BOTH completion signals.
   * `chat:idle` ends programmatic/animated activity; `message:complete` ends an
   * ordinary streamed turn (it is what clears `Input.isSubmitting`). Listening to
   * only one leaves the other class of turn unable to flush the queue. Firing is
   * idempotent — `_fireNow` clears the slot before invoking it — so a turn that
   * emits both signals still fires exactly once.
   */
  _subscribe() {
    if (this._onIdle) return; // already listening (swap keeps the same listener)
    this._onIdle = () => this._fireNow();
    for (const evt of TURN_END_EVENTS) this._bus()?.on?.(evt, this._onIdle);
  }

  /** @private Remove the turn-end listeners. */
  _unsubscribe() {
    if (!this._onIdle) return;
    for (const evt of TURN_END_EVENTS) this._bus()?.off?.(evt, this._onIdle);
    this._onIdle = null;
  }

  /**
   * @private
   * Fire the pending slot exactly once: revert placeholder + pending visual first
   * (once the echo bubble is in the transcript, the transcript tells the story),
   * then run the fire action. The slot is cleared BEFORE invoking `fire` so a
   * re-entrant click during the fired turn starts a fresh queue cycle.
   */
  _fireNow() {
    const pending = this._pending;
    this._unsubscribe();
    this._pending = null;
    if (!pending) return;
    this._getPillRow()?.clearPillPending?.();
    this._revertPlaceholder();
    try {
      pending.fire();
    } catch (_err) { /* a bad fire must not break the dispatcher */ }
  }

  /**
   * @private
   * Revert the queued-window placeholder to the default lifecycle
   * (`clearCustomPlaceholder()` + `updatePlaceholder()` — the mode-placeholder system).
   */
  _revertPlaceholder() {
    const input = this._getInput();
    if (!input) return;
    input.clearCustomPlaceholder?.();
    input.updatePlaceholder?.();
  }
}
